import { CurecallTooltip, TrashIcon } from '@honestica/curecall-ui'
import type { TMedicalTeamComment } from './medical-team-comments.type'
import { twMerge } from 'src/utils/tw-merge.util'
import { useTranslation } from 'react-i18next'
import { useSession } from 'src/providers/auth.provider'

export type TMedicalTeamCommentItemProps = {
  medicalTeamComment: TMedicalTeamComment
  selectedComment?: { id: number; comment: string }
  onDelete: (medicalComment: TMedicalTeamComment) => void
  onSelect: ({ id, comment }: { id: number; comment: string }) => void
  canWrite?: boolean
  isPatientFollowupEnded?: boolean
}

const MedicalTeamCommentItem = ({
  medicalTeamComment,
  selectedComment,
  onDelete,
  onSelect,
  canWrite,
  isPatientFollowupEnded,
}: TMedicalTeamCommentItemProps) => {
  const { currentUser } = useSession()

  const { t } = useTranslation()

  const userTitleToShow = ['Dr.', 'Pr.']
  const MedicalCommentAuthor = `${
    medicalTeamComment.User.title && userTitleToShow.includes(medicalTeamComment.User.title)
      ? t('common.abbreviated-user-titles', {
          title: medicalTeamComment.User.title.replace('.', ''),
        })
      : ''
  } ${medicalTeamComment.User.firstname} ${medicalTeamComment.User.lastname}`

  const currentUserIsAuthor = medicalTeamComment.User.id === currentUser?.id

  const canEdit = canWrite && !isPatientFollowupEnded

  const handleSelectComment = (medicalCommentId: number, medicalComment: string) => {
    currentUserIsAuthor && canEdit && onSelect({ id: medicalCommentId, comment: medicalComment })
  }

  return (
    <div className="c-relative c-w-full c-flex c-flex-col c-gap-2 c-border-l-2 c-border-l-grey-700 c-pl-4 c-py-4">
      <span className="c-absolute c-top-0 c-left-[-0.45rem] c-w-3 c-h-3 c-bg-grey-700 c-rounded-full" />

      <span className="c-absolute c-top-[-0.75rem] c-mb-6">
        <span className="c-font-bold c-text-sm">{MedicalCommentAuthor.trim()}</span>
        <span className="c-text-xl c-font-bold c-text-grey-500 c-align-bottom">{' . '}</span>
        <CurecallTooltip content={medicalTeamComment.dateLabelTooltip} wrapperClassName="tw-inline-block">
          <span className="c-text-sm c-font-thin c-text-grey-500">{medicalTeamComment.dateLabel} </span>
        </CurecallTooltip>
      </span>

      <div className="c-flex c-flex-row c-items-center c-gap-4 c-mt-2 c-mb-4">
        <p
          onClick={() => handleSelectComment(medicalTeamComment.id, medicalTeamComment.comment)}
          className={twMerge(
            'c-w-full c-font-thin c-py-2 c-px-3 c-rounded-md c-text-grey-500 c-bg-grey-100 c-border c-border-grey-500 c-text-xs c-m-0',
            currentUserIsAuthor && canEdit && 'c-text-grey-700 c-bg-white c-cursor-pointer',
            medicalTeamComment.id === selectedComment?.id && 'c-bg-primary-100 c-border-primary-201',
          )}
        >
          {medicalTeamComment.comment}
        </p>
        {currentUserIsAuthor && canEdit && (
          <button
            className="c-h-6 c-w-6 c-p-1 c-text-xl c-rounded-full c-cursor-pointer c-bg-grey-700 c-text-white hover:tw-bg-grey-700 hover:tw-text-white hover:tw-shadow-xl focus:tw-bg-grey-700"
            aria-label={t('components.medical-team-comments.delete-comment')}
            onClick={() => onDelete(medicalTeamComment)}
          >
            <TrashIcon />
          </button>
        )}
      </div>
    </div>
  )
}

export default MedicalTeamCommentItem
